import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";

import { messagesApi } from "@/api/messages";
import { logger } from "@/utils/logger";

const detectContactInfo = (text) => {
  if (!text) return false;
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phoneRegex = /[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}/;
  return emailRegex.test(text) || phoneRegex.test(text);
};

const Thread = ({ user }) => {
  const location = useLocation();
  const threadId = location.pathname.split('/').pop();

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchThread();
  }, [threadId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchThread = async () => {
    try {
      const data = await messagesApi.thread(threadId);
      setThread(data.thread);
      setMessages(data.messages);

      if (data.thread?.is_blocked) {
        toast.warning("Messaging is disabled for this conversation.");
      }
    } catch (error) {
      logger.error("Failed to fetch thread:", error);
      toast.error("Failed to load conversation");
    } finally {
      setLoading(false);
    }
  };

  const isBreeder = thread?.breeder_id === user?.user_id;
  const otherPartyName = isBreeder ? thread?.buyer_name : thread?.breeder_name;

  const isBlocked = Boolean(thread?.is_blocked);
  const blockedByUserId = thread?.blocked_by_user_id;

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    if (detectContactInfo(newMessage.trim())) {
      toast.warning("For your safety, avoid sharing phone numbers or emails in chat. Keep communication on Breedling.");
    }

    setSending(true);
    try {
      await messagesApi.sendMessage(threadId, newMessage.trim());
      setNewMessage("");
      fetchThread();
    } catch (error) {
      logger.error("Failed to send message:", error);
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    try {
      await messagesApi.close(threadId);
      toast.success("Thread closed");
      fetchThread();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to close thread");
    }
  };

  const handleReopen = async () => {
    try {
      await messagesApi.reopen(threadId);
      toast.success("Thread reopened");
      fetchThread();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to reopen thread");
    }
  };

  const handleReport = async () => {
    if (reportSubmitting) return;

    setReportSubmitting(true);
    try {
      await messagesApi.report(threadId, {
        reason: reportReason,
        details: reportDetails.trim() || undefined,
      });
      toast.success("Report submitted");
      setShowReport(false);
      setReportDetails("");
      setReportReason("spam");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to submit report");
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleBlock = async () => {
    if (!isBreeder) return;
    try {
      await messagesApi.block(threadId);
      toast.success("User blocked. Thread closed.");
      fetchThread();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to block user");
    }
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#A1A1AA]">Thread not found</p>
          <Link to="/messages" className="btn-secondary text-sm mt-4 inline-block">Back to Inbox</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-[#050505] flex flex-col">
      <div className="fixed top-16 left-0 right-0 z-40 bg-[#050505] border-b border-white/5 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/messages')} className="text-[#A1A1AA] hover:text-[#EDEDED]">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <img src={thread.listing_photo || "https://via.placeholder.com/40"} alt="" className="w-10 h-10 rounded-lg object-cover" />
          <div className="flex-1 min-w-0">
            <p className="text-[#EDEDED] font-medium truncate">{otherPartyName}</p>
            <p className="text-xs text-[#A1A1AA] truncate">{thread.listing_title}</p>
          </div>

          {isBreeder && thread.status !== 'closed' && (
            <button onClick={handleClose} className="text-xs text-[#A1A1AA] hover:text-red-400">Close Thread</button>
          )}
          {isBreeder && thread.status === 'closed' && (
            <button onClick={handleReopen} className="text-xs text-[#D4AF37]">Reopen</button>
          )}

          <button onClick={() => setShowReport(true)} className="text-xs text-[#A1A1AA] hover:text-[#EDEDED]">Report</button>

          {isBreeder && (
            <button onClick={handleBlock} className="text-xs text-red-400 hover:text-red-300">Block</button>
          )}
        </div>
      </div>

      <div className="flex-1 pt-32 pb-24 px-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <Link
            to={`/listing/${thread.listing_id}`}
            className="glass-card p-4 flex items-center gap-4 mb-6 hover:border-[#D4AF37]/30 transition-colors"
          >
            <img src={thread.listing_photo || "https://via.placeholder.com/80"} alt="" className="w-20 h-20 rounded-lg object-cover" />
            <div>
              <p className="text-[#EDEDED] font-medium">{thread.listing_title}</p>
              <p className="text-sm text-[#A1A1AA]">View Listing →</p>
            </div>
          </Link>

          {showReport && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowReport(false)}>
              <div className="glass-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl text-[#EDEDED]">Report conversation</h3>
                  <button onClick={() => setShowReport(false)} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <label className="block text-sm text-[#A1A1AA] mb-2">Reason</label>
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="input-minimal bg-transparent mb-4">
                  <option value="spam">Spam</option>
                  <option value="scam">Scam / fraud</option>
                  <option value="harassment">Harassment</option>
                  <option value="inappropriate">Inappropriate content</option>
                  <option value="other">Other</option>
                </select>

                <label className="block text-sm text-[#A1A1AA] mb-2">Details (optional)</label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  className="w-full h-28 bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-[#EDEDED] placeholder:text-[#A1A1AA]/50 focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                  placeholder="Add any context that helps moderation review."
                />

                <div className="flex gap-4 mt-4">
                  <button onClick={() => setShowReport(false)} className="btn-secondary flex-1">Cancel</button>
                  <button
                    onClick={handleReport}
                    disabled={reportSubmitting}
                    className="btn-gold flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {reportSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {messages.map((msg) => {
            const isMine = msg.sender_id === user?.user_id;
            return (
              <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] p-4 rounded-2xl ${
                    isMine ? 'bg-[#D4AF37] text-black rounded-br-sm' : 'bg-[#1A1A1A] text-[#EDEDED] rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                  <p className={`text-xs mt-1 ${isMine ? 'text-black/60' : 'text-[#71717A]'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {isBlocked ? (
        <div className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-white/5 px-6 py-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#A1A1AA] text-sm">
              {blockedByUserId === thread?.breeder_id ? "You can’t message this breeder." : "Messaging is disabled for this conversation."}
            </p>
          </div>
        </div>
      ) : thread.status !== 'closed' ? (
        <div className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-white/5 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-full px-5 py-3 text-[#EDEDED] placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]/50"
              data-testid="message-input"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="btn-gold rounded-full p-3 disabled:opacity-50"
              data-testid="send-btn"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-white/5 px-6 py-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#A1A1AA] text-sm">This conversation has been closed.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Thread;
